import { ComponentProps } from 'react';
import {
  Radio as AriaRadio,
  RadioGroup as AriaRadioGroup,
  Button,
  RadioGroupProps,
} from 'react-aria-components';
import { twMerge } from 'tailwind-merge';
import {
  RadioIndicatorContainerVariants,
  radioIndicator,
  radioIndicatorContainer,
} from './variants';
import { Typography } from '../../ui';
import { IconInfoCircle } from '@maia/icons';

export function RadioGroup(props: RadioGroupProps) {
  const { children, ...rest } = props;
  return <AriaRadioGroup {...rest}>{children}</AriaRadioGroup>;
}

export type RadioProps = {
  className?: string;
  label?: string;
  description?: string;
  error?: boolean;
  onMoreInfo?: () => void;
} & Omit<ComponentProps<typeof AriaRadio>, 'children' | 'className'> &
  RadioIndicatorContainerVariants;

export function Radio(props: RadioProps) {
  const {
    label,
    type,
    description,
    size,
    className,
    onMoreInfo = undefined,
    isDisabled = false,
    error = false,
    ...rest
  } = props;

  return (
    <AriaRadio
      className={twMerge(
        'flex gap-x-12 text-grayscale500 cursor-pointer group data-[disabled="true"]:cursor-not-allowed',
        className,
      )}
      isDisabled={isDisabled}
      {...rest}
    >
      <div
        className={twMerge(
          radioIndicatorContainer({ size, type }),
          error && '!border-error300',
        )}
      >
        <span className={radioIndicator({ size, type })} />
      </div>

      {label && (
        <div className="flex flex-col gap-y-8 flex-1">
          <Typography
            variant="body-m"
            weight="regular"
            className="text-grayscale500 group-data-[disabled='true']:text-grayscale400"
          >
            {label}
          </Typography>

          {description && (
            <Typography
              variant="body-xs"
              weight="regular"
              className="text-grayscale300 group-data-[disabled='true']:text-grayscale400"
            >
              {description}
            </Typography>
          )}
        </div>
      )}

      {onMoreInfo && (
        <Button className="h-fit" onPress={onMoreInfo}>
          <IconInfoCircle className="fill-info300 size-24" />
        </Button>
      )}
    </AriaRadio>
  );
}
